<template lang="pug">
  div#capture
    .card-header.bg-white
      div.form-inline
        .form-group.mr-3
          select.form-control(@change="updatePoint", v-model="selectedPoint")
            option(v-for="point in points" :key="point.k" :value="point.k") {{ point.v }}
        .form-group.mr-3
          .custom-select(@click="toggleDropdown" style="height:38px; border:1px solid #ced4da; border-radius:4px; background:#fff; cursor:pointer; display:flex; align-items:center; padding:0 12px;")
            .select-display {{ getSelectedPathName }}
            .select-items(v-show="isDropdownOpen")
              .search-box(@click.stop)
                input.form-control(type="text", v-model="pathSearch", placeholder="URLを検索", @input="filterPaths", @click.stop)
              .select-options
                .select-option(
                  v-for="path in filteredPaths.filter(p => p && p.k !== undefined && p.k !== null)"
                  :key="String(path.k)"
                  @click.stop="selectPath(path)"
                )
                  span.name {{ path.v }}
                  span.separator |
                  span.path {{ tag_domain.domain + String(path.k) }}
      div.form-inline.mt-2
        .form-group.mr-3
          select.form-control(@change="handleDateSelection", v-model="selectedDateOption")
            option(v-for="date in dateOptions" :key="date.k" :value="date.k") {{ date.v }}
        .form-group.mr-3
          vuejs-datepicker(@input="handleDateChange" v-model="startDate", :format="DatePickerFormat", :language="language", input-class="form-control")
        div.mr-3 ~
        .form-group
          vuejs-datepicker(@input="handleDateChange" v-model="endDate", :format="DatePickerFormat", :language="language", input-class="form-control")
        .form-group.ml-3
          .device-buttons
            button.mr-3(:class="['btn', selectedDeviceType === 1 ? 'btn-primary' : 'border']", @click="setDeviceType(1)")
              i.fas.fa-mobile-alt
              | SP
            button.mr-3(:class="['btn', selectedDeviceType === 0 ? 'btn-primary' : 'border']", @click="setDeviceType(0)")
              i.fas.fa-desktop
              | PC
        .form-group.ml-3.w-4
          i.fas.fa-lg.fa-download(title="レポートダウンロード", @click="captureScreenshot")

    .card-body.bg-white
      div#flow-container.flow-container
        div#pan-wrapper
      UserFlowDetailPanel(
        v-if="currentDetailNode"
        :currentDetailNode="currentDetailNode"
        :selectedPoint="selectedPoint"
        @setStartPoint="setStartPoint"
        @setEndPoint="setEndPoint"
      )

</template>

<script>
import vuejsDatepicker from 'vuejs-datepicker';
import { ja } from 'vuejs-datepicker/dist/locale';
import { jsPlumb } from 'jsplumb';
import axios from '../../plugins/axios'
import UserFlowDetailPanel from './user_flow_detail_panel.vue'
import { DatePickerFormat, dateOptions, formatDate, getDateRange } from '../../utils/dateUtils'
import domtoimage from 'dom-to-image-more';

export default {
  components: { 
    vuejsDatepicker,
    UserFlowDetailPanel
  },
  props: {
    initialData: {
      type: Object,
      required: true,
      validator: (value) => {
        return (
          value.tag_domain !== undefined &&
          value.path !== undefined &&
          value.point !== undefined &&
          value.start_node !== undefined &&
          value.next_nodes !== undefined
        );
      }
    }
  },
  data() {
    return {
      DatePickerFormat,
      language: ja,
      jpInstance: null,
      renderedNodes: {},
      nodeCounter: 0,
      lastClickedNodeId: null,
      currentDetailNode: null,
      pathSearch: '',
      filteredPaths: [],
      isDropdownOpen: false,

      // 共通スタイル定義
      nodeBaseStyle: {
        position: 'absolute',
        padding: '4px 10px',
        border: '1px solid #333',
        borderRadius: '8px',
        width: '240px',
        height: '66px',
        zIndex: '5',
        transition: 'border-color 0.3s ease'
      },
      countBaseStyle: {
        position: 'absolute',
        padding: '0px 10px',
        width: '240px',
        textAlign: 'left',
        fontSize: '18px',
        fontWeight: 'normal',
        color: '#2c3e50',
        pointerEvents: 'none',
        zIndex: '10'
      },

      // ノードの位置計算用の定数
      nodeSpacing: {
        horizontal: 300,
        vertical: 120
      },

      tag_domain: null,
      start_node: null,
      next_nodes: [],

      startDate: new Date(),
      endDate: new Date(),
      selectedPoint: 'start',
      selectedDateOption: 'today',
      selectedPath: '',
      selectedDeviceType: 0,

      dateOptions,
      points: [
        { k: 'start', v: '始点' },
        { k: 'end', v: '終点' }
      ],
      paths: []
    };
  },
  created() {
    this.initializeData();
  },
  computed: {
    hasAnyPopupIds() {
      return (
        this.currentDetailNode &&
        (this.currentDetailNode.pop_up_ids && this.currentDetailNode.pop_up_ids.length > 0) ||
        (this.currentDetailNode.area_pop_up_ids && this.currentDetailNode.area_pop_up_ids.length > 0) ||
        (this.currentDetailNode.overlay_ids && this.currentDetailNode.overlay_ids.length > 0) ||
        (this.currentDetailNode.scenario_popup_ids && this.currentDetailNode.scenario_popup_ids.length > 0)
      );
    },
    getSelectedPathName() {
      const selected = this.paths.find(p => p && p.k !== undefined && p.k !== null && String(p.k) === this.selectedPath);
      return selected ? (this.tag_domain?.domain || '') + String(selected.k) : 'パスを選択';
    }
  },
  methods: {
    initializeData() {
      this.tag_domain = this.initialData.tag_domain;
      this.selectedPath = this.initialData.path;
      this.selectedPoint = this.initialData.point;
      this.startDate = this.initialData.start_date;
      this.endDate = this.initialData.end_date;
      this.start_node = this.initialData.start_node;
      this.next_nodes = this.initialData.next_nodes;
      this.paths = this.initialData.heatmaps || [];
      this.filteredPaths = [...this.paths].filter(p => p && p.k !== undefined && p.k !== null);

      // DOMの完全なレンダリングを待つ
      this.$nextTick(() => {
        // 追加の遅延を設けてDOMの準備を確実にする
        setTimeout(() => {
          this.initFlowDiagram();
        }, 100);
      });
    },
    initFlowDiagram() {
      if (this.jpInstance) this.jpInstance.reset();
      this.jpInstance = jsPlumb.getInstance({
        Container: 'pan-wrapper',
        Connector: ['Flowchart', { cornerRadius: 0 }],
        PaintStyle: { stroke: '#333', strokeWidth: 2 },
        Endpoint: 'Blank',
        Anchors: ['Right', 'Left']
      });
      this.renderFlowNodes();
      this.currentDetailNode = this.start_node;
    },
    calculateNodePosition(baseX, baseY, index, level) {
      return {
        x: baseX + (this.nodeSpacing.horizontal * level),
        y: baseY + (this.nodeSpacing.vertical * index)
      };
    },
    renderFlowNodes() {
      const container = document.getElementById('pan-wrapper');
      container.innerHTML = '';
      this.renderedNodes = {};
      if (!this.start_node) return;

      const startId = this.generateNodeId(this.start_node.path);
      const startPosition = this.calculateNodePosition(50, 50, 0, 0);
      this.createNodeWithConnection(
        startId,
        this.start_node.path,
        this.start_node.title,
        startPosition.x,
        startPosition.y,
        this.start_node.pv_count,
        null,
        0
      );
      this.renderedNodes[startId].data = {
        ...this.renderedNodes[startId].data,
        ...this.start_node
      };

      Object.values(this.next_nodes).forEach((child, index) => {
        const childId = this.generateNodeId(child.path + child.timestamp);
        const childPosition = this.calculateNodePosition(50, 50, index, 1);
        this.createNodeWithConnection(
          childId,
          child.path,
          child.title,
          childPosition.x,
          childPosition.y,
          child.pv_count,
          startId,
          1
        );
        this.renderedNodes[childId].data = {
          ...this.renderedNodes[childId].data,
          ...child
        };
      });

      const exitId = this.generateNodeId("exit");
      const exitPosition = this.calculateNodePosition(50, 50, Object.keys(this.next_nodes).length, 1);
      this.createExitNodeWithConnection(
        exitId,
        exitPosition.x,
        exitPosition.y,
        this.start_node.exit_count,
        this.start_node.exit_percent,
        this.start_node.exit_percent_from_start_node,
        startId,
        1
      );

      this.jpInstance.repaintEverything();
    },
    createNodeWithConnection(id, path, title, x, y, count, parentId, level) {
      const nodeData = this.initializeNodeData(path, title, count);
      if (!this.validateNodeData(nodeData)) {
        console.error('Invalid node data:', nodeData);
        return;
      }

      const parent_count = parentId ? this.renderedNodes[parentId]?.data?.pv_count || 0 : 0;
      this.createNode(id, path, title, x, y, count, parent_count);
      this.renderedNodes[id] = {
        data: nodeData,
        level,
        parentId
      };
      if (parentId) {
        this.jpInstance.connect({ source: parentId, target: id, anchors: ['Right', 'Left'] });
      }
    },
    createExitNodeWithConnection(id, x, y, count, percent, percent_from_start_node, parentId, level) {
      const nodeData = this.initializeExitNodeData(count, percent, percent_from_start_node);
      if (!this.validateNodeData(nodeData)) {
        console.error('Invalid exit node data:', nodeData);
        return;
      }

      const parent_count = parentId ? this.renderedNodes[parentId]?.data?.pv_count || 0 : 0;
      this.createExitNode(id, x, y, count, percent, percent_from_start_node, parent_count);
      this.renderedNodes[id] = {
        data: nodeData,
        level,
        parentId
      };
      if (parentId) {
        this.jpInstance.connect({ source: parentId, target: id, anchors: ['Right', 'Left'] });
      }
    },
    setupNodeEventHandlers(node, id) {
      // クリックイベント
      node.addEventListener('click', () => this.handleNodeClickDynamic(id));

      // マウスオーバーイベント
      node.addEventListener('mouseenter', () => {
        node.style.border = '2px solid #4285f4';
      });

      // マウスアウトイベント
      node.addEventListener('mouseleave', () => {
        if (this.lastClickedNodeId !== id) {
          node.style.border = '1px solid #333';
        }
      });
    },
    createNode(id, path, title, x, y, count = 0, parent_count = 0) {
      const container = document.getElementById('pan-wrapper');
      const domain = this.tag_domain?.domain || '';
      const fullUrl = `${domain}${path}`;

      const countDiv = document.createElement('div');
      countDiv.id = `count-${id}`;
      const percentage = parent_count > 0 ? Number((count / parent_count * 100).toFixed(1)) : 0;
      countDiv.innerText = `セッション: ${count} (${percentage.toFixed(1)}%)`;
      Object.assign(countDiv.style, {
        ...this.countBaseStyle,
        top: `${y - 26}px`,
        left: `${x}px`
      });

      const node = document.createElement('div');
      node.classList.add('node');
      node.id = id;

      node.innerHTML = `
        <div class="node-title" style="font-weight: normal; font-size: 18px; width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${title}</div>
        <div style="width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          <a href="${fullUrl}" target="_blank" class="node-link"
            style="font-size: 18px; color: #007bff; text-decoration: underline;">
            ${path}
          </a>
        </div>
      `;

      Object.assign(node.style, {
        ...this.nodeBaseStyle,
        top: `${y}px`,
        left: `${x}px`,
        background: '#fff',
        cursor: 'default'
      });

      // ノード全体のクリックイベント
      node.addEventListener('click', (e) => {
        // リンクをクリックした場合は遷移を許可
        if (e.target.classList.contains('node-link')) {
          return true;
        }
        // リンク以外の部分をクリックした場合はhandleNodeClickDynamicを実行
        e.preventDefault();
        e.stopPropagation();
        this.handleNodeClickDynamic(id);
      });

      // マウスオーバーイベント
      node.addEventListener('mouseenter', () => {
        if (this.lastClickedNodeId !== id) {
          node.style.border = '2px solid #4285f4';
        }
      });

      // マウスアウトイベント
      node.addEventListener('mouseleave', () => {
        if (this.lastClickedNodeId !== id) {
          node.style.border = '1px solid #333';
        }
      });

      container.appendChild(countDiv);
      container.appendChild(node);
    },
    createExitNode(id, x, y, count = 0, percent = 0, percent_from_start_node = 0, parent_count = 0) {
      const container = document.getElementById('pan-wrapper');

      const countDiv = document.createElement('div');
      countDiv.id = `count-${id}`;
      const percentage = parent_count > 0 ? Number((count / parent_count * 100).toFixed(1)) : 0;
      countDiv.innerText = `セッション: ${count} (${percentage.toFixed(1)}%)`;
      Object.assign(countDiv.style, {
        ...this.countBaseStyle,
        top: `${y - 26}px`,
        left: `${x}px`
      });

      const node = document.createElement('div');
      node.classList.add('node');
      node.id = id;

      if (this.selectedPoint === 'start') {
        node.innerHTML = `
          <div style="font-weight: normal; font-size: 18px; width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">離脱率: ${Number(percent).toFixed(1)}%</div>
          <div style="font-weight: normal; font-size: 18px; width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">始点からの離脱率: ${Number(percent_from_start_node).toFixed(1)}%</div>
        `;
      } else {
        node.innerHTML = `
          <div style="font-weight: normal; font-size: 18px; width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">初回アクセス率: ${Number(percent).toFixed(1)}%</div>
        `;
      }

      Object.assign(node.style, {
        ...this.nodeBaseStyle,
        top: `${y}px`,
        left: `${x}px`,
        background: '#D9D9D9',
        pointerEvents: 'none',
        cursor: 'default'
      });

      container.appendChild(countDiv);
      container.appendChild(node);
    },
    isDescendantOf(childId, ancestorId) {
      let current = this.renderedNodes[childId];
      while (current && current.parentId) {
        if (current.parentId === ancestorId) return true;
        current = this.renderedNodes[current.parentId];
      }
      return false;
    },
    resetNodeBorderColor() {
      if (this.lastClickedNodeId) {
        const lastNode = document.getElementById(this.lastClickedNodeId);
        if (lastNode) {
          lastNode.style.border = '1px solid #333';
        }
      }
    },
    setNodeBorderColor(nodeId) {
      const node = document.getElementById(nodeId);
      if (node) {
        node.style.border = '3px solid #4285f4';
      }
    },
    async handleNodeClickDynamic(id) {
      this.resetNodeBorderColor();
      this.setNodeBorderColor(id);
      this.lastClickedNodeId = id;

      const baseNode = this.renderedNodes[id];
      if (!baseNode) return;

      this.currentDetailNode = {
        ...baseNode.data,
        avg_stay_time: baseNode.data.avg_stay_time || 0
      };

      const baseEl = document.getElementById(id);
      if (!baseEl) return;

      const baseX = parseInt(baseEl.style.left);
      const baseY = parseInt(baseEl.style.top);
      const baseLevel = baseNode.level;

      Object.keys(this.renderedNodes).forEach(nodeId => {
        const nodeInfo = this.renderedNodes[nodeId];
        if (nodeInfo.level > baseLevel && !this.isDescendantOf(nodeId, id)) {
          const el = document.getElementById(nodeId);
          const countEl = document.getElementById(`count-${nodeId}`);
          if (el) el.remove();
          if (countEl) countEl.remove();
          this.jpInstance.removeAllEndpoints(nodeId);
          delete this.renderedNodes[nodeId];
        }
      });

      const heatmapIdSequence = [];
      let currentId = id;
      while (currentId) {
        const node = this.renderedNodes[currentId];
        if (!node) break;
        heatmapIdSequence.unshift(node.data.heatmap_ids);
        currentId = node.parentId;
      }

      let children = [];
      try {
        const res = await axios.get('/users/analytics/user_flows/fetch_next_nodes', {
          params: {
            heatmap_id_sequence: heatmapIdSequence,
            point: this.selectedPoint,
            path: this.selectedPath,
            start_date: this.formatDate(this.startDate),
            end_date: this.formatDate(this.endDate),
            device: this.selectedDeviceType
          }
        });
        children = res.data;
      } catch (err) {
        console.error('次ページ取得失敗:', err);
      }

      Object.values(children).forEach((child, i) => {
        const childId = this.generateNodeId(child.path + child.timestamp);
        if (this.renderedNodes[childId]) return;

        const childPosition = this.calculateNodePosition(baseX, baseY, i, 1);
        this.createNodeWithConnection(
          childId,
          child.path,
          child.title,
          childPosition.x,
          childPosition.y,
          child.pv_count,
          id,
          baseLevel + 1
        );
        this.renderedNodes[childId].data = {
          ...this.renderedNodes[childId].data,
          ...child
        };
      });

      if (Object.keys(children).length > 0 || baseNode.data.exit_count >= 0) {
        const exitId = this.generateNodeId("exit");
        const exitPosition = this.calculateNodePosition(baseX, baseY, Object.keys(children).length, 1);
        this.createExitNodeWithConnection(
          exitId,
          exitPosition.x,
          exitPosition.y,
          baseNode.data.exit_count,
          baseNode.data.exit_percent,
          baseNode.data.exit_percent_from_start_node,
          id,
          baseLevel + 1
        );
      }

      this.jpInstance.repaintEverything();
    },
    generateNodeId(base) {
      this.nodeCounter += 1;
      return `node-${this.nodeCounter}-${base}`;
    },
    handleDateChange() {
      this.fetchFlowData();
    },
    handleDateSelection() {
      const { start, end } = getDateRange(this.selectedDateOption);
      this.startDate = start;
      this.endDate = end;
      this.fetchFlowData();
    },
    updatePoint() {
      this.fetchFlowData();
    },
    setDeviceType(type) {
      this.selectedDeviceType = type;
      this.fetchFlowData();
    },
    formatDate(date) {
      const d = new Date(date);
      return `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`;
    },
    fetchFlowData() {
      axios.get('/users/analytics/user_flows.json', {
        params: {
          path: this.selectedPath,
          point: this.selectedPoint,
          start_date: this.formatDate(this.startDate),
          end_date: this.formatDate(this.endDate),
          device: this.selectedDeviceType
        }
      })
      .then(response => {
        const data = response.data;
        this.tag_domain = data.tag_domain;
        this.selectedPath = data.path;
        this.selectedPoint = data.point;
        this.start_node = data.start_node;
        this.next_nodes = data.next_nodes;
        this.paths = data.heatmaps || [];
        this.filteredPaths = [...this.paths].filter(p => p && p.k !== undefined && p.k !== null);

        this.initFlowDiagram();
      })
      .catch(error => {
        console.error('ユーザーフローデータ取得エラー:', error);
      });
    },
    async captureScreenshot() {
      const element = document.getElementById('capture');
      if (!element) {
        console.error('キャプチャ対象の要素が見つかりません');
        return;
      }

      try {
        // キャプチャ前の準備
        await this.prepareForCapture(element);
        
        // スクリーンショットの取得
        const dataUrl = await this.takeScreenshot(element);
        
        // ダウンロード処理
        this.downloadScreenshot(dataUrl);
      } catch (error) {
        console.error('スクリーンショット取得エラー:', error);
        alert('スクリーンショットの取得に失敗しました。もう一度お試しください。');
      } finally {
        // キャプチャ後のクリーンアップ
        await this.cleanupAfterCapture(element);
      }
    },

    async prepareForCapture(element) {
      // アニメーションを無効化
      element.classList.add('no-animations');
      document.body.classList.add('no-animations');

      // jsPlumbの再描画を一時的に無効化
      if (this.jpInstance) {
        this.originalRepaintEverything = this.jpInstance.repaintEverything;
        this.jpInstance.repaintEverything = () => {};
      }

      // フォントの読み込みを待機
      await document.fonts.ready;
      await new Promise(resolve => setTimeout(resolve, 200));
    },

    async takeScreenshot(element) {
      // 外部CSSファイルを一時的に無効化
      const disabledStylesheets = this.disableExternalStylesheets();
      
      try {
        return await domtoimage.toPng(element, {
          quality: 1.0,
          bgcolor: '#ffffff',
          style: {
            'transform': 'none',
            'transform-origin': '0 0',
            'font-family': 'inherit',
            'font-size': 'inherit',
            'line-height': 'inherit',
            'letter-spacing': 'inherit',
            'color': 'inherit'
          },
          filter: (node) => {
            // SVG要素を除外しない
            return true;
          },
          imagePlaceholder: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNmZmYiLz48L3N2Zz4='
        });
      } finally {
        // 外部CSSファイルを再度有効化
        this.enableExternalStylesheets(disabledStylesheets);
      }
    },

    disableExternalStylesheets() {
      const disabledStylesheets = [];
      document.querySelectorAll("link[rel='stylesheet']").forEach((link) => {
        if (
          link.href.includes('fonts.googleapis') ||
          link.href.includes('ionicframework') ||
          link.href.includes('fontawesome')
        ) {
          link.disabled = true;
          disabledStylesheets.push(link);
        }
      });
      return disabledStylesheets;
    },

    enableExternalStylesheets(disabledStylesheets) {
      disabledStylesheets.forEach(link => {
        link.disabled = false;
      });
    },

    downloadScreenshot(dataUrl) {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = this.generateFileName();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async cleanupAfterCapture(element) {
      // jsPlumbの再描画を元に戻す
      if (this.jpInstance && this.originalRepaintEverything) {
        this.jpInstance.repaintEverything = this.originalRepaintEverything;
      }

      // アニメーションを有効化
      element.classList.remove('no-animations');
      document.body.classList.remove('no-animations');
    },

    generateFileName() {
      const formatDate = (date) => {
        const d = new Date(date);
        return `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`;
      };
      const start = formatDate(this.startDate);
      const end = formatDate(this.endDate);
      let url = this.tag_domain?.domain + this.selectedPath
      url = url.replace(/^https?:\/\//, '').replace(/[:\/]/g, '');
      return `${start}~${end}_${url}_${this.selectedPoint}_${this.selectedDeviceType === '1' ? 'sp' : 'pc'}.png`;
    },
    enablePan() {
      const wrapper = document.getElementById('pan-wrapper');
      let isPanning = false;
      let startX = 0, startY = 0;
      let currentX = 0, currentY = 0;

      wrapper.style.cursor = 'grab';

      wrapper.addEventListener('mousedown', (e) => {
        isPanning = true;
        startX = e.clientX - currentX;
        startY = e.clientY - currentY;
        wrapper.style.cursor = 'grabbing';
      });

      window.addEventListener('mousemove', (e) => {
        if (!isPanning) return;
        currentX = e.clientX - startX;
        currentY = e.clientY - startY;
        wrapper.style.transform = `translate(${currentX}px, ${currentY}px)`;
      });

      window.addEventListener('mouseup', () => {
        isPanning = false;
        wrapper.style.cursor = 'grab';
      });
    },
    setStartPoint() {
      if (!this.currentDetailNode) return;
      this.selectedPath = this.currentDetailNode.path;
      this.selectedPoint = "start";
      this.fetchFlowData();
    },
    setEndPoint() {
      if (!this.currentDetailNode) return;
      this.selectedPath = this.currentDetailNode.path;
      this.selectedPoint = "end";
      this.fetchFlowData();
    },
    // ノードデータの初期化
    initializeNodeData(path, title, count) {
      return {
        path,
        title,
        pv_count: count,
        exit_count: 0,
        exit_percent: 0,
        exit_percent_from_start_node: 0,
        avg_stay_time: 0,
        heatmap_ids: [],
        pop_up_ids: [],
        area_pop_up_ids: [],
        overlay_ids: [],
        scenario_popup_ids: [],
        cv_tag_ids: []
      };
    },

    // 離脱ノードデータの初期化
    initializeExitNodeData(count, percent, percent_from_start_node) {
      return {
        path: "exit",
        title: "離脱",
        pv_count: count,
        exit_count: count,
        exit_percent: percent,
        exit_percent_from_start_node: percent_from_start_node,
        avg_stay_time: 0,
        heatmap_ids: [],
        pop_up_ids: [],
        area_pop_up_ids: [],
        overlay_ids: [],
        scenario_popup_ids: [],
        cv_tag_ids: []
      };
    },

    // ノードデータの検証
    validateNodeData(data) {
      const requiredFields = ['path', 'title', 'pv_count'];
      return requiredFields.every(field => data[field] !== undefined);
    },
    filterPaths() {
      if (!this.pathSearch) {
        this.filteredPaths = [...this.paths].filter(p => p && p.k !== undefined && p.k !== null);
        return;
      }
      const searchLower = this.pathSearch.toLowerCase();
      const domain = this.tag_domain?.domain || '';
      this.filteredPaths = this.paths.filter(path => {
        if (!path || path.k === undefined || path.k === null || !path.v) return false;
        const fullPath = domain + String(path.k);
        return (typeof path.v === 'string' && path.v.toLowerCase().includes(searchLower)) ||
               (String(path.k).toLowerCase().includes(searchLower)) ||
               (typeof fullPath === 'string' && fullPath.toLowerCase().includes(searchLower));
      });
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.isDropdownOpen) {
        this.pathSearch = '';
        this.filteredPaths = [...this.paths].filter(p => p && p.k !== undefined && p.k !== null);
      }
    },
    selectPath(path) {
      this.selectedPath = path.k;
      this.selectedPoint = "start";
      this.isDropdownOpen = false;
      this.fetchFlowData();
    }
  },
  mounted() {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.initFlowDiagram();
        this.enablePan();
      });
    });

    // ドロップダウン外をクリックした時に閉じる
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.custom-select')) {
        this.isDropdownOpen = false;
      }
    });
  }
};
</script>

<style scoped>
.no-animations,
.no-animations * {
  transition: none !important;
  animation: none !important;
}

.flow-container {
  position: relative;
  overflow: hidden;
  min-height: 880px;
  background-color: #f9f9f9;
  border: 1px solid #ccc;
}

#pan-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 10000px;
  height: 10000px;
  transform: translate(0px, 0px);
}

.jtk-connector { z-index: 1; }
.jtk-endpoint { z-index: 2; }
.dragHover { border: 2px solid #4285f4; }

.node {
  position: absolute;
  padding: 4px 10px;
  border: 1px solid #333;
  border-radius: 8px;
  width: 240px;
  height: 66px;
  z-index: 5;
  transition: border-color 0.3s ease;
  background: #fff;
  cursor: default;
  min-width: 120px;
  text-align: center;
}

.node-link {
  cursor: pointer;
  display: inline;
}

.node-link:hover {
  text-decoration: underline !important;
}

.node:hover {
  border: 2px solid #4285f4;
}

.node.selected {
  border: 3px solid #4285f4;
}

.device-buttons {
  display: flex;
  align-items: center;
}

.form-inline {
  display: flex;
  width: 100%;
  align-items: center;
}

.form-group:has(.custom-select) {
  min-width: 0;
  flex: 1;
}

.custom-select {
  position: relative;
  width: 100%;
  height: 38px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0 12px;
}

.select-display {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #495057;
}

.select-display .path,
.select-display .name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-display .separator {
  color: #333;
  flex: 0 0 auto;
}

.custom-select:hover {
  border-color: #80bdff;
}

.select-items {
  position: absolute;
  background-color: #fff;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1001;
  border: 1px solid #ced4da;
  border-radius: 4px;
  margin-top: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.search-box {
  padding: 8px;
  border-bottom: 1px solid #ced4da;
  background-color: #fff;
}

.search-box input {
  width: 100%;
  height: 32px;
  padding: 4px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.select-options {
  max-height: 300px;
  overflow-y: auto;
}

.select-option {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.select-option .path,
.select-option .name {
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-option .name {
  flex: 1;
  min-width: 100px;
}

.select-option .path {
  flex: 2;
  text-align: left;
}

.select-option .separator {
  color: #333;
  flex: 0 0 auto;
  margin: 0 4px;
}

.select-option:last-child {
  border-bottom: none;
}

.select-option:hover {
  background-color: #f8f9fa;
}

.card-header.bg-white {
  background: #fff;
  border-bottom: 1px solid #dee2e6;
  padding: 0.75rem 1.25rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.device-buttons button {
  width: 80px;
}

.fa-download {
  cursor: pointer;
  padding-left: 10px;
}
</style>
